import { Calendar, Check, ChevronDown, ChevronRight, ChevronUp, Clock, Plus, Users } from 'react-feather';
import { Fragment, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import groupBy from 'lodash/groupBy';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import pick from 'lodash/pick';
import times from 'lodash/times';
import uniq from 'lodash/uniq';
import upperFirst from 'lodash/upperFirst';

import {
  AssignmentType, BusinessRegistrationInput, CreateRegistrationInput, DiscountStrategy,
} from '__generated__/graphql';

import { Availability, Quantities } from '../useQuantities';
import {
  CheckoutStep, Event, Product, Promotion, Ticket, TimeSlot, getAdditionalProducts, getTicketsForSale,
} from '../helpers';
import { EmbedContext } from '../../Common/EmbedProvider';
import { mergeUrlParams, replaceUrlParams, resizedImageUrl, toggleInList } from '../../../common/helpers';
import { useEvent } from '../../Common/EventProvider';
import CheckoutContext from '../CheckoutContext';
import PriceRange from '../../../common/Common/PriceRange';
import ScheduledDiscountLabel, { shouldRenderScheduledDiscountLabel } from '../ScheduledDiscountLabel';
import SoldOutLabel from '../SoldOutLabel';
import SportIcon from '../../../common/Common/SportIcon';
import UI from '../../../common/UI';
import VolumeDiscountInfo from '../VolumeDiscountInfo';
import useCheckoutUrlState from '../useCheckoutUrlState';
import useLocale from '../../../common/useLocale';
import useResaleState from '../../../common/useResaleState';
import useTimeSlotFormatter from '../../../common/Tickets/useTimeSlotFormatter';

const TicketSelector = () => {
  const { t } = useTranslation();
  const { event, ticketCategories } = useEvent();
  const [{ showCategories }] = useCheckoutUrlState();
  const { parseDate, formatDate } = useLocale();

  const {
    setRegistrations,
    setBusinessRegistrations,
    quantities,
    availability,
    validators: { [CheckoutStep.Tickets]: { errors } },
    personalisation,
  } = useContext(CheckoutContext);

  const selectedTicketCategoryIds = useMemo(
    () => ticketCategories
      // Initially expand a ticket category if is the only one or if one its tickets has been selected.
      .filter((ticketCategory) => ticketCategories.length === 1 || quantities.ticketCategories[ticketCategory.id] > 0)
      .map((ticketCategory) => ticketCategory.id),
    [ticketCategories, quantities],
  );

  const getInitialTicketCategoryIds = useCallback(() => uniq([
    ...showCategories,
    ...selectedTicketCategoryIds,
  ]), [showCategories, selectedTicketCategoryIds]);

  const [activeTicketCategoryIds, setActiveTicketCategoryIds] = useState(getInitialTicketCategoryIds);

  const ticketCategoriesRef = useRef(ticketCategories.map((ticketCategory) => ticketCategory.id).join());

  const showStartDates = ticketCategories.some(
    (ticketCategory) => ticketCategory.start_date && ticketCategory.start_date !== event.start_date,
  );

  /**
   * Reset the active ticket categories when the list of ticket categories changes.
   * This can happen when an invitation code is entered.
   */
  useEffect(() => {
    const ticketCategoryIds = ticketCategories.map((ticketCategory) => ticketCategory.id).join();

    if (ticketCategoryIds !== ticketCategoriesRef.current) {
      setActiveTicketCategoryIds(getInitialTicketCategoryIds);
      ticketCategoriesRef.current = ticketCategoryIds;
    }
  }, [ticketCategories, getInitialTicketCategoryIds]);

  const handleBusinessTicketChange = useCallback(
    (quantity: number, ticket: Ticket, promotion: Promotion, timeSlot?: TimeSlot) => {
      setBusinessRegistrations((registrations) => {
        // Find the index of the promotion and time slot combination (if it already exists)
        const index = registrations.findIndex(
          (registration) => registration.promotion.id === promotion.id && registration.time_slot?.id === timeSlot?.id,
        );
        const registration = registrations[index];
        const newRegistrations = [...registrations];

        // Add automatic ticket fees and required products as upgrades
        const upgrades = getAdditionalProducts(promotion, event.products_for_sale, true);

        const newRegistration: BusinessRegistrationInput = {
          ...registration,
          promotion: { id: promotion.id },
          time_slot: timeSlot ? { id: timeSlot.id } : null,
          upgrades: registration?.upgrades || upgrades,
          quantity,
        };

        if (index > -1) {
          if (newRegistration.quantity === 0) {
            newRegistrations.splice(index, 1);
          } else {
            newRegistrations[index] = newRegistration;
          }
        } else {
          newRegistrations.push(newRegistration);
        }

        return newRegistrations;
      }, event);
    },
    [setBusinessRegistrations, event],
  );

  const handleChange = useCallback(
    (quantity: number, ticket: Ticket, promotion: Promotion, timeSlot?: TimeSlot) => {
      if (ticket.business) {
        handleBusinessTicketChange(quantity, ticket, promotion, timeSlot);
      } else {
        setRegistrations(
          (registrations) => insertRegistration(
            registrations,
            ticket,
            promotion,
            timeSlot,
            quantity * ticket.units,
            event.products_for_sale,
            ticket.assignment_type !== AssignmentType.afterward || personalisation.requirePersonalisationGlobally,
          ),
          event,
        );
      }
    },
    [event, handleBusinessTicketChange, setRegistrations, personalisation.requirePersonalisationGlobally],
  );

  /**
   * Only returns the quantities that are needed by one TicketSelect. This helps to make sure we don't
   * needlessly rerender all TicketSelects when a ticket is added, which can get quite expensive.
   */
  interface TicketQuantitiesAndAvailability { quantities: Quantities, availability: Availability }
  const getTicketQuantitiesAndAvailability = (ticket: Ticket): TicketQuantitiesAndAvailability => {
    const promotionIds: string[] = [];

    if (ticket.current_discount) {
      // Include promotion IDs of all tickets with the same discount, because the DiscountInfo
      // component requires them (some discounts are applicable to combinations of tickets)
      const discount = ticket.current_discount;

      getTicketsForSale(event).forEach((ticket) => {
        if (ticket.current_discount?.id === discount.id) {
          ticket.promotions_for_sale.forEach((promotion) => {
            promotionIds.push(promotion.id);
          });
        }
      });

      event.products_for_sale.forEach((product) => {
        if (product.current_discount?.id === discount.id) {
          product.promotions_for_sale.forEach((promotion) => {
            promotionIds.push(promotion.id);
          });
        }
      });
    } else {
      // If there is no discount, the TicketSelect only needs the quantities for this specific ticket
      ticket.promotions_for_sale.forEach((promotion) => {
        promotionIds.push(promotion.id);
      });
    }

    return {
      quantities: {
        ticketCategories: {}, // Individual TicketSelects don't need this.
        tickets: {
          [ticket.id]: quantities.tickets[ticket.id],
        },
        promotions: pick(quantities.promotions, promotionIds),
        timeSlots: pick(quantities.timeSlots, promotionIds),
        products: {},
        productVariants: {},
        purchases: pick(quantities.purchases, promotionIds),
        hasBusinessTicket: ticket.business,
        hasConsumerTicket: !ticket.allow_companies,
      },
      availability: {
        promotions: pick(availability.promotions, promotionIds),
        timeSlots: pick(availability.timeSlots, promotionIds),
        productVariants: {},
      },
    };
  };

  return (
    <UI.FormGrid sc={{ gutter: 0.5 }}>
      {ticketCategories.map((ticketCategory) => (
        <UI.Div key={ticketCategory.id}>
          {ticketCategories.length > 1 && (
            <UI.NavCard
              to={mergeUrlParams({
                showCategories: toggleInList(activeTicketCategoryIds, ticketCategory.id),
              })}
              onClick={() => setActiveTicketCategoryIds(toggleInList(activeTicketCategoryIds, ticketCategory.id))}
              sc={{
                background: quantities.ticketCategories[ticketCategory.id] > 0 ? 'secondary.25' : 'gray.100/30',
                outline: quantities.ticketCategories[ticketCategory.id] > 0 ? 'secondary.100' : 'gray.150',
                borderRadius: 'lg',
                spacing: 'sm',
                py: 1.5,
              }}
              replace
              resetScrollPosition={false}
              title={ticketCategory.title}
            >
              <UI.GridContainer
                sc={{ columns: '24px 1fr 30px', gutter: 0.5, alignVertical: 'center' }}
                style={{ minHeight: 36 }}
              >
                <UI.Icon sc={{ color: 'secondary', size: 1.75 }}>
                  <SportIcon type={ticketCategory.sport_type} />
                </UI.Icon>
                <UI.Div>
                  <UI.H4>
                    {ticketCategory.title}
                  </UI.H4>
                  {((showStartDates && ticketCategory.start_date) || ticketCategory.formatted_distances) && (
                    <UI.Div sc={{ small: true, color: 'gray.600' }}>
                      <UI.Delimit>
                        {showStartDates && ticketCategory.start_date && formatDate(
                          parseDate(ticketCategory.start_date, { timezone: 'UTC' }),
                          { format: 'display_time_slot_date' },
                        )}
                        {ticketCategory.formatted_distances.join(', ')}
                      </UI.Delimit>
                    </UI.Div>
                  )}
                </UI.Div>
                <UI.Div>
                  <UI.Icon
                    sc={{
                      color: quantities.ticketCategories[ticketCategory.id] > 0 ? 'secondary' : 'gray.600',
                      size: 2,
                    }}
                  >
                    {quantities.ticketCategories[ticketCategory.id] > 0
                      ? <Check strokeWidth={1} />
                      : (activeTicketCategoryIds.includes(ticketCategory.id)
                        ? <ChevronUp strokeWidth={1} />
                        : <ChevronDown strokeWidth={1} />)}
                  </UI.Icon>
                </UI.Div>
              </UI.GridContainer>
            </UI.NavCard>
          )}
          <UI.AnimateHeight isVisible={activeTicketCategoryIds.includes(ticketCategory.id)}>
            <UI.InputGroup sc={{ invalid: !!errors?.tickets }}>
              <UI.FormGrid sc={{ gutter: 0.5, pt: ticketCategories.length > 1 ? 2 : 0 }}>
                {ticketCategory.tickets_for_sale.map((ticket) => (
                  <TicketSelect
                    event={event}
                    ticket={ticket}
                    {...getTicketQuantitiesAndAvailability(ticket)}
                    onChange={handleChange}
                    key={ticket.id}
                  />
                ))}
              </UI.FormGrid>
              <UI.ErrorMessages attribute={t('selector.tickets')} errors={errors.tickets} sc={{ mt: 2 }} />
            </UI.InputGroup>
          </UI.AnimateHeight>
        </UI.Div>
      ))}
    </UI.FormGrid>
  );
};

interface TicketSelectProps {
  event: Event;
  ticket: Ticket;
  quantities: Quantities;
  availability: Availability;
  onChange: (quantity: number, ticket: Ticket, promotion: Promotion, timeSlot?: TimeSlot) => void;
}

/**
 * Memoized using Lodash' isEqual() method because the quantities and availabilities are not stable.
 * This prevents unnecessary rerenders which is especially useful when there are many tickets.
 */
const TicketSelect = memo(
  ({ event, ticket, quantities, availability, onChange }: TicketSelectProps) => {
    const { t } = useTranslation();
    const { formatNumber, parseDate } = useLocale();
    const { scrollToTop } = useContext(EmbedContext);
    const formatTimeSlot = useTimeSlotFormatter();

    const quantity = quantities.tickets[ticket.id];
    const selected = quantity > 0;
    const defaultPromotion = ticket.promotions_for_sale[0];
    const ticketHasAmount = Math.max(...ticket.promotions_for_sale.map(({ amount }) => amount)) > 0;

    const [showingQuantitySelect, setShowingQuantitySelect] = useState(selected);

    /**
     * If the form state gets changed from somewhere else, update the UI accordingly.
     */
    useEffect(() => {
      if (selected) {
        setShowingQuantitySelect(true);
      }
    }, [selected]);

    /** Only show prices next to promotions if they have different prices */
    const showPromotionPrices = useMemo(
      () => uniq(map(ticket.promotions_for_sale, 'amount')).length > 1,
      [ticket],
    );

    const options = Math.max(1, ticket.upcoming_time_slots.length) * ticket.promotions_for_sale.length;

    const showPreregistration = requirePreregistration(ticket);

    const canBeSelected = useMemo(() => {
      if (showPreregistration && options > 1) {
        return true;
      }

      if (ticket.current_max_per_order === 0 || ticket.is_sold_out) {
        return false;
      }

      for (let i = 0; i < ticket.promotions_for_sale.length; i++) {
        const promotion = ticket.promotions_for_sale[i];

        for (let j = 0; j < ticket.upcoming_time_slots.length; j++) {
          const timeSlot = ticket.upcoming_time_slots[j];

          if (availability.timeSlots[promotion.id][timeSlot.id] > 0) {
            return true;
          }
        }

        if (ticket.upcoming_time_slots.length <= 1) {
          if (availability.promotions[promotion.id] > 0) {
            return true;
          }
        }
      }

      return false;
    }, [showPreregistration, options, ticket, availability]);

    const toggle = () => {
      if (options === 1) {
        if (quantity === 0) {
          onChange(1, ticket, defaultPromotion, ticket.upcoming_time_slots[0]);
        }

        setShowingQuantitySelect(true);
      } else {
        setShowingQuantitySelect((showing) => !showing);
      }
    };

    const onDeselect = useCallback(() => {
      setShowingQuantitySelect(false);
    }, [setShowingQuantitySelect]);

    const { publicResale } = useResaleState(event);

    const showResale = ticket.is_sold_out && publicResale.enabled && ticket.allow_resale
      && ticket.cached_registrations_for_sale_count > 0;

    const showWaitlist = ticket.is_sold_out && event.enable_waitlist && !showResale;

    const showInterestList = ticket.is_sold_out && event.enable_interest_list && event.interest_list
      && !showResale && !showWaitlist;

    // If the ticket cannot be selected because the ticket category's maximum is reached, then display 'Full'.
    // For group tickets, apply rounding so that the number of spots left is always divisible by the group size.
    const spotsLeft = !ticket.is_sold_out && ticket.published_availability !== null
      ? (canBeSelected || quantity > 0
        ? ticket.published_availability - (ticket.published_availability % ticket.units)
        : 0)
      : null;

    const hasScheduledDiscountLabel = ticket.current_discount
      && shouldRenderScheduledDiscountLabel({
        discount: ticket.current_discount,
        promotions: ticket.promotions_for_sale,
        parseDate,
      });

    const hasVolumeDiscountInfo = quantity > 0 && ticket.current_discount?.strategy === DiscountStrategy.volume;
    const hasDescription = !!ticket.description;
    const hasLabelRow = (ticket.units > 1 || ticket.upcoming_time_slots.length === 1 || spotsLeft !== null
      || hasScheduledDiscountLabel);

    return (
      <UI.NavCard
        sc={{
          outline: selected ? 'secondary' : 'gray.200',
          borderWidth: selected ? 2 : 1,
          borderRadius: 'lg',
          spacing: 'sm',
          transitionPadding: false,
        }}
        onClick={canBeSelected
          ? () => toggle()
          : ((showPreregistration && options === 1) || showResale || showWaitlist || showInterestList
            ? () => scrollToTop()
            : undefined)}
        to={(showPreregistration && options === 1
          ? `/${event.id}/preregistration/${ticket.id}/${ticket.promotions_for_sale[0].id}`
          : (showResale
            ? `/${event.id}/resale${replaceUrlParams({ ticket: ticket.id })}`
            : (showWaitlist
              ? `/${event.id}/waitlist/${ticket.id}`
              : (showInterestList
                ? `/${event.id}/interest`
                : undefined
              ))))}
        tabIndex={-1}
      >
        {ticket.image && (
          <UI.Image sc={{ ratio: 2, mb: [2.25, 3] }}>
            <UI.Div>
              <img
                src={resizedImageUrl(ticket.image.url, 800, 'webp')}
                alt={ticket.title}
              />
            </UI.Div>
          </UI.Image>
        )}
        <UI.GridContainer sc={{ columns: '1fr fit-content(200px)', gutter: 0.5 }}>
          <UI.Div>
            <UI.H4>
              <UI.Span sc={{ color: ticket.is_sold_out ? 'gray.500' : undefined }}>
                {ticket.title}
                {ticket.promotions_for_sale.length === 1 && !defaultPromotion.standard && (
                  <>
                    <UI.Span sc={{ muted: true }}> • </UI.Span>
                    {defaultPromotion.title}
                  </>
                )}
              </UI.Span>
            </UI.H4>
            <UI.FlexContainer
              sc={{
                alignItems: 'center',
                wrap: 'wrap',
                spaceX: 2,
                color: ticket.is_sold_out ? 'gray.500' : undefined,
              }}
            >
              <UI.Div>
                <PriceRange prices={ticket.promotions_for_sale} />
              </UI.Div>
              {ticket.business && ticket.vat_percentage > 0 && ticketHasAmount && (
              <UI.Small sc={{ muted: true }}>
                {t('selector.includes_vat_percentage', { percentage: formatNumber(ticket.vat_percentage) })}
              </UI.Small>
              )}
            </UI.FlexContainer>
          </UI.Div>
          <UI.Div>
            {!ticket.is_sold_out && (
              <>
                {(showPreregistration && options === 1) ? (
                  <UI.LinkButton
                    to={`/${event.id}/preregistration/${ticket.id}/${ticket.promotions_for_sale[0].id}`}
                    sc={{ brand: 'secondary', size: 'sm' }}
                    aria-label={t('selector.preregister_item', { item: ticket.title })}
                    onClick={() => scrollToTop()}
                  >
                    {t('selector.preregister')}
                    {' '}
                    <UI.Icon>
                      <ChevronRight />
                    </UI.Icon>
                  </UI.LinkButton>
                ) : (
                  <>
                    {(!showingQuantitySelect || options > 1) && (
                      <UI.Button
                        onClick={() => toggle()}
                        sc={{ brand: 'secondary', active: showingQuantitySelect, size: 'sm' }}
                        disabled={!canBeSelected}
                        aria-label={options === 1
                          ? t('selector.add_item', { title: ticket.title })
                          : t('selector.choose_item', { title: ticket.title })}
                      >
                        {options === 1 && (
                          <>
                            <UI.Icon>
                              <Plus />
                            </UI.Icon>
                            {' '}
                            {t('selector.add')}
                          </>
                        )}
                        {options > 1 && (
                          <>
                            {t('selector.choose')}
                            {' '}
                            <UI.Icon>
                              {showingQuantitySelect ? <ChevronUp /> : <ChevronDown />}
                            </UI.Icon>
                          </>
                        )}
                      </UI.Button>
                    )}
                    {showingQuantitySelect && options === 1 && (
                      <PromotionSelect
                        eventId={event.id}
                        ticket={ticket}
                        promotion={ticket.promotions_for_sale[0]}
                        promotionQuantity={quantities.promotions[ticket.promotions_for_sale[0].id]}
                        promotionAvailability={availability.promotions[ticket.promotions_for_sale[0].id]}
                        timeSlotQuantities={quantities.timeSlots[ticket.promotions_for_sale[0].id]}
                        timeSlotAvailabilities={availability.timeSlots[ticket.promotions_for_sale[0].id]}
                        onChange={onChange}
                        onDeselect={onDeselect}
                        showPrice={showPromotionPrices}
                      />
                    )}
                  </>
                )}
              </>
            )}
            {ticket.is_sold_out && (
              <>
                {showResale && (
                  <UI.LinkButton
                    to={`/${event.id}/resale${replaceUrlParams({ ticket: ticket.id })}`}
                    aria-label={t('selector.ticket_resale_item', { item: ticket.title })}
                    sc={{ size: 'sm' }}
                  >
                    {t('ticket_resale')}
                    {' ('}
                    {formatNumber(ticket.cached_registrations_for_sale_count)}
                    )
                    {' '}
                    <UI.Icon>
                      <ChevronRight />
                    </UI.Icon>
                  </UI.LinkButton>
                )}
                {showWaitlist && (
                  <UI.LinkButton
                    to={`/${event.id}/waitlist/${ticket.id}`}
                    onClick={() => scrollToTop()}
                    aria-label={t('selector.waitlist_item', { item: ticket.title })}
                    sc={{ size: 'sm' }}
                  >
                    {t('waitlist.waitlist')}
                    {' '}
                    <UI.Icon>
                      <ChevronRight />
                    </UI.Icon>
                  </UI.LinkButton>
                )}
                {showInterestList && (
                  <UI.LinkButton
                    to={`/${event.id}/interest`}
                    onClick={() => scrollToTop()}
                    sc={{ size: 'sm' }}
                  >
                    {t('interest_list.newsletter')}
                    {' '}
                    <UI.Icon>
                      <ChevronRight />
                    </UI.Icon>
                  </UI.LinkButton>
                )}
                {!showResale && !showWaitlist && !showInterestList && (
                  <SoldOutLabel title={ticket.title} />
                )}
              </>
            )}
          </UI.Div>
        </UI.GridContainer>

        {!ticket.is_sold_out && options > 1 && (
          <UI.AnimateHeight isVisible={showingQuantitySelect}>
            <UI.GridContainer
              sc={{
                // Increase the hitbox of this div by applying negative margin and add z-index,
                // so that clicking anywhere on this div does not cause the NavCard to collapse.
                mt: [2.25, 3],
                mx: [-2.25, -3],
                mb: [-2.25, -3],
                gutter: 2 / 3,
                px: [2.25, 3],
                pb: [2.25, 3],
                relative: true,
              }}
              style={{ zIndex: 300 }}
            >
              {options > 1 && ticket.promotions_for_sale.map((promotion) => (
                <PromotionSelect
                  eventId={event.id}
                  ticket={ticket}
                  promotion={promotion}
                  promotionQuantity={quantities.promotions[promotion.id]}
                  promotionAvailability={availability.promotions[promotion.id]}
                  timeSlotQuantities={quantities.timeSlots[promotion.id]}
                  timeSlotAvailabilities={availability.timeSlots[promotion.id]}
                  onChange={onChange}
                  onDeselect={onDeselect}
                  showPrice={showPromotionPrices}
                  key={promotion.id}
                />
              ))}
              {(hasVolumeDiscountInfo || hasDescription || hasLabelRow) && (
                <UI.HR />
              )}
            </UI.GridContainer>
          </UI.AnimateHeight>
        )}

        {hasVolumeDiscountInfo && (
          <UI.FadeIn sc={{ mt: [2.25, 3] }}>
            <VolumeDiscountInfo event={event} discount={ticket.current_discount} quantities={quantities} />
          </UI.FadeIn>
        )}

        {hasDescription && (
          <UI.Div sc={{ mt: [2.25, 3], color: 'gray.600' }}>
            <UI.HTML html={ticket.description} />
          </UI.Div>
        )}

        {hasLabelRow && (
          <UI.FlexContainer
            sc={{
              alignItems: 'center', wrap: 'wrap', gap: 0.25, small: true, mt: [2.25, 3],
            }}
          >
            {ticket.units > 1 && (
              <UI.Label>
                <UI.Icon>
                  <Users />
                </UI.Icon>
                {' '}
                {formatNumber(ticket.units)}
              </UI.Label>
            )}
            {ticket.upcoming_time_slots.length === 1 && (
              <UI.Label>
                <UI.Icon>
                  <Clock />
                </UI.Icon>
                {' '}
                <UI.Delimit>
                  {formatTimeSlot(ticket.upcoming_time_slots[0])}
                  {ticket.upcoming_time_slots[0].title}
                </UI.Delimit>
              </UI.Label>
            )}
            {spotsLeft !== null && (
              <SpotsLeftLabel spotsLeft={spotsLeft} />
            )}
            {hasScheduledDiscountLabel && (
              <ScheduledDiscountLabel
                discount={ticket.current_discount}
                promotions={ticket.promotions_for_sale}
              />
            )}
          </UI.FlexContainer>
        )}
      </UI.NavCard>
    );
  },
  (prevProps, newProps) => isEqual(prevProps, newProps),
);

TicketSelect.displayName = 'TicketSelect';

interface PromotionSelectProps {
  eventId: string;
  ticket: Ticket;
  promotion: Promotion;
  promotionQuantity: number;
  promotionAvailability: number;
  timeSlotQuantities: { [timeSlotId: string]: number; };
  timeSlotAvailabilities: { [timeSlotId: string]: number; };
  onChange: (quantity: number, ticket: Ticket, promotion: Promotion, timeSlot?: TimeSlot) => void;
  onDeselect: () => void;
  showPrice?: boolean;
}

/**
 * Memoized using Lodash' isEqual() method because timeSlotQuantities and timeSlotAvailabilities are not stable.
 * This prevents unnecessary rerenders which is especially useful when there are many promotions or time slots.
 */
const PromotionSelect = memo(
  ({
    eventId, ticket, promotion, promotionQuantity, promotionAvailability, timeSlotQuantities, timeSlotAvailabilities,
    onChange, onDeselect, showPrice = true,
  }: PromotionSelectProps) => {
    const { t } = useTranslation();
    const { formatCurrency } = useLocale();
    const { scrollToTop } = useContext(EmbedContext);

    const [focused, setFocused] = useState(false);

    const handleChange = useCallback(
      (quantity: number) => {
        onChange(
          quantity,
          ticket,
          promotion,
          ticket.upcoming_time_slots.length === 1 ? ticket.upcoming_time_slots[0] : undefined,
        );

        if (ticket.promotions_for_sale.length === 1 && ticket.upcoming_time_slots.length <= 1
          && quantity === 0 && !focused) {
          onDeselect();
        }
      },
      [onChange, ticket, promotion, focused, onDeselect],
    );

    const handleFocus = useCallback(() => {
      setFocused(true);
    }, [setFocused]);

    const handleBlur = useCallback(() => {
      setFocused(false);
    }, [setFocused]);

    const groupedTimeSlots = groupBy(ticket.upcoming_time_slots, 'start_date');

    const isMultiDate = Object.keys(groupedTimeSlots).length > 1;

    const getTimeSlotQuantitiesAndAvailability = (timeSlots: TimeSlot[]) => {
      const timeSlotIds = timeSlots.map(({ id }) => id);

      return {
        timeSlotQuantities: pick(timeSlotQuantities, timeSlotIds),
        timeSlotAvailabilities: pick(timeSlotAvailabilities, timeSlotIds),
      };
    };

    return (
      <>
        {ticket.promotions_for_sale.length > 1 && (
          <UI.HR />
        )}

        {(ticket.upcoming_time_slots.length <= 1 || ticket.promotions_for_sale.length > 1) && (
          <UI.GridContainer
            sc={{
              columns: ticket.promotions_for_sale.length > 1 && ticket.upcoming_time_slots.length <= 1
                ? '1fr fit-content(180px)' : '1fr',
              alignVertical: 'center',
              gutter: 0.5,
            }}
          >
            {ticket.promotions_for_sale.length > 1 && (
              <UI.GridContainer
                sc={{
                  columns: ticket.upcoming_time_slots.length > 1 ? '1fr 1fr' : '1fr',
                  gutter: 0,
                }}
              >
                <UI.Div>
                  {ticket.upcoming_time_slots.length <= 1 && (
                    <UI.InputLabel>{promotion.title || t('selector.standard')}</UI.InputLabel>
                  )}
                  {ticket.upcoming_time_slots.length > 1 && (
                    <UI.Legend>{promotion.title || t('selector.standard')}</UI.Legend>
                  )}
                </UI.Div>
                {showPrice && (
                  <UI.Div sc={{ textAlign: ticket.upcoming_time_slots.length > 1 ? 'right' : undefined }}>
                    {promotion.amount === 0 && t('selector.free')}
                    {promotion.amount > 0 && formatCurrency(promotion.current_amount)}
                  </UI.Div>
                )}
              </UI.GridContainer>
            )}
              {promotion.is_sold_out ? (
                <UI.Div>
                  <SoldOutLabel title={`${ticket.title} / ${promotion.title || t('selector.standard')}`} />
                </UI.Div>
              ) : (
                ticket.upcoming_time_slots.length <= 1 && (
                  <UI.Div>
                    {requirePreregistration(ticket) ? (
                      <UI.LinkButton
                        to={`/${eventId}/preregistration/${ticket.id}/${promotion.id}`}
                        sc={{ brand: 'secondary', size: 'sm' }}
                        aria-label={t('selector.preregister_item', { item: `${ticket.title} / ${promotion.title || t('selector.standard')}` })}
                        onClick={() => scrollToTop()}
                      >
                        {t('selector.preregister')}
                        {' '}
                        <UI.Icon>
                          <ChevronRight />
                        </UI.Icon>
                      </UI.LinkButton>
                    ) : (
                      <UI.QuantitySelect
                        quantity={promotionQuantity}
                        onChange={handleChange}
                        maxQuantity={promotionQuantity + promotionAvailability}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        title={`${ticket.title} / ${promotion.title || t('selector.standard')}`}
                        size="sm"
                      />
                    )}
                  </UI.Div>
                )
              )}
          </UI.GridContainer>
        )}

        {!promotion.is_sold_out && ticket.upcoming_time_slots.length > 1
          && Object.values(groupedTimeSlots).map((timeSlots) => (
            <Fragment key={timeSlots[0].id}>
              {ticket.promotions_for_sale.length === 1 && (
                <UI.HR />
              )}
              <TimeSlotList
                eventId={eventId}
                ticket={ticket}
                promotion={promotion}
                timeSlots={timeSlots}
                isMultiDate={isMultiDate}
                {...getTimeSlotQuantitiesAndAvailability(timeSlots)}
                onChange={onChange}
                handleFocus={handleFocus}
                handleBlur={handleBlur}
              />
            </Fragment>
          ))}
      </>
    );
  },
  (prevProps, newProps) => isEqual(prevProps, newProps),
);

PromotionSelect.displayName = 'PromotionSelect';

interface TimeSlotListProps {
  eventId: string;
  ticket: Ticket;
  promotion: Promotion;
  timeSlots: TimeSlot[];
  isMultiDate: boolean;
  timeSlotQuantities: { [timeSlotId: string]: number; };
  timeSlotAvailabilities: { [timeSlotId: string]: number; };
  onChange: (quantity: number, ticket: Ticket, promotion: Promotion, timeSlot?: TimeSlot) => void;
  handleFocus: () => void;
  handleBlur: () => void;
}

/**
 * Memoized using Lodash' isEqual() method because the quantities and availabilities are not stable.
 * This prevents unnecessary rerenders which is especially useful when there are many time slots.
 */
const TimeSlotList = memo(
  ({
    eventId, ticket, promotion, timeSlots, isMultiDate, timeSlotQuantities, timeSlotAvailabilities,
    onChange, handleFocus, handleBlur,
  }: TimeSlotListProps) => {
    const { t } = useTranslation();
    const { formatDate, parseDate } = useLocale();
    const formatTimeSlot = useTimeSlotFormatter();

    const timeSlotGroupIsSelected = timeSlots.filter((timeSlot) => timeSlotQuantities[timeSlot.id] > 0).length > 0;
    const timeSlotGroupIsSoldOut = timeSlots.filter((timeSlot) => !timeSlot.is_sold_out).length === 0;

    const isGrouped = isMultiDate && timeSlots.length > 1;
    const [showingTimeSlots, setShowingTimeSlots] = useState(!isGrouped || timeSlotGroupIsSelected);

    const toggleTimeSlots = () => setShowingTimeSlots((showing) => !showing);

    const combinedAvailability = timeSlots.reduce((sum, timeSlot) => (
      sum === null || timeSlot.published_availability === null ? null : (
        sum + timeSlot.published_availability
      )
    ), 0);

    const spotsLeft = !isGrouped || combinedAvailability !== null
      ? (combinedAvailability - (combinedAvailability % ticket.units))
      : null;

    return (
      <>
        {isGrouped && (
          <UI.GridContainer sc={{ columns: '1fr fit-content(120px)', alignVertical: 'center', gutter: 0.5 }}>
            <UI.GridContainer sc={{ columns: '12px 1fr', gutter: 0.5 }}>
              <UI.Div>
                <UI.Icon sc={{ muted: true }}>
                  <Calendar />
                </UI.Icon>
              </UI.Div>
              <UI.Div>
                <UI.Strong sc={{ block: true }}>
                  {upperFirst(formatDate(
                    parseDate(timeSlots[0].start_date, { format: 'internal_date' }),
                    { format: 'display_time_slot_date' },
                  ))}
                </UI.Strong>
                {spotsLeft !== null && (
                  <UI.Span sc={{ small: true }}>
                    <SpotsLeftLabel spotsLeft={spotsLeft} />
                  </UI.Span>
                )}
              </UI.Div>
            </UI.GridContainer>
            <UI.Div>
              {!timeSlotGroupIsSoldOut && (
                <UI.Button
                  onClick={() => toggleTimeSlots()}
                  sc={{ brand: 'secondary', active: showingTimeSlots, size: 'sm' }}
                  disabled={timeSlotGroupIsSelected}
                  aria-label={t('selector.choose_item', {
                    title: `${ticket.title} / ${promotion.title || t('selector.standard')} / ${formatTimeSlot({ ...timeSlots[0], start_time: null })}`,
                  })}
                >
                  {t('selector.choose')}
                  {' '}
                  <UI.Icon>
                    {!showingTimeSlots && <ChevronDown />}
                    {showingTimeSlots && <ChevronUp />}
                  </UI.Icon>
                </UI.Button>
              )}
              {timeSlotGroupIsSoldOut && (
                <SoldOutLabel />
              )}
            </UI.Div>
          </UI.GridContainer>
        )}
        <UI.AnimateHeight isVisible={showingTimeSlots}>
          <UI.GridContainer sc={{ gutter: 0.5 }}>
            {timeSlots.map((timeSlot) => (
              <TimeSlotSelect
                eventId={eventId}
                ticket={ticket}
                promotion={promotion}
                timeSlot={timeSlot}
                quantity={timeSlotQuantities[timeSlot.id]}
                showDate={isMultiDate && !isGrouped}
                onChange={onChange}
                maxQuantity={timeSlotQuantities[timeSlot.id] + timeSlotAvailabilities[timeSlot.id]}
                availability={timeSlotAvailabilities[timeSlot.id]}
                onFocus={handleFocus}
                onBlur={handleBlur}
                key={timeSlot.id}
              />
            ))}
          </UI.GridContainer>
        </UI.AnimateHeight>
      </>
    );
  },
  (prevProps, newProps) => isEqual(prevProps, newProps),
);

TimeSlotList.displayName = 'TimeSlotList';

interface TimeSlotSelectProps {
  eventId: string;
  ticket: Ticket;
  promotion: Promotion;
  timeSlot: TimeSlot;
  quantity: number;
  showDate?: boolean;
  maxQuantity?: number;
  availability: number;
  onChange: (quantity: number, ticket: Ticket, promotion: Promotion, timeSlot?: TimeSlot) => void;
  onFocus: () => void;
  onBlur: () => void;
}

/**
 * Memoized because there are potentially many time slots, especially if there are also multiple promotions.
 */
const TimeSlotSelect = memo(({
  eventId, ticket, promotion, timeSlot, quantity, showDate = false, maxQuantity, availability,
  onChange, onFocus, onBlur,
}: TimeSlotSelectProps) => {
  const { t } = useTranslation();
  const formatTimeSlot = useTimeSlotFormatter();

  const handleChange = useCallback(
    (quantity: number) => {
      onChange(quantity, ticket, promotion, timeSlot);
    },
    [onChange, ticket, promotion, timeSlot],
  );

  return (
    <UI.GridContainer sc={{ columns: '1fr fit-content(180px)', alignVertical: 'center', gutter: 0.5 }}>
      <UI.GridContainer sc={{ columns: '12px 1fr', gutter: 0.5 }}>
        <UI.Div>
          <UI.Icon sc={{ muted: true }}><Clock /></UI.Icon>
        </UI.Div>
        <UI.Div sc={{ muted: availability === 0 && !requirePreregistration(ticket) }}>
          <UI.InputLabel>
            {formatTimeSlot({ ...timeSlot, multi_date: showDate })}
          </UI.InputLabel>
          {timeSlot.title}
        </UI.Div>
      </UI.GridContainer>

      <UI.FlexContainer sc={{ justifyContent: 'flex-end' }}>
        {timeSlot.is_sold_out ? (
          <SoldOutLabel title={`${ticket.title} / ${promotion.title || t('selector.standard')} / ${formatTimeSlot(timeSlot)}`} />
        ) : (
          requirePreregistration(ticket) ? (
            <UI.LinkButton
              to={`/${eventId}/preregistration/${ticket.id}/${promotion.id}?timeSlot=${timeSlot.id}`}
              sc={{ brand: 'secondary', size: 'sm' }}
              aria-label={t('selector.preregister')}
            >
              {t('selector.preregister')}
              {' '}
              <UI.Icon>
                <ChevronRight />
              </UI.Icon>
            </UI.LinkButton>
          ) : (
            <UI.QuantitySelect
              quantity={quantity}
              onChange={handleChange}
              maxQuantity={maxQuantity}
              onFocus={onFocus}
              onBlur={onBlur}
              title={`${ticket.title} / ${promotion.title || t('selector.standard')} / ${formatTimeSlot(timeSlot)}`}
              size="sm"
            />
          )
        )}
      </UI.FlexContainer>
    </UI.GridContainer>
  );
});

TimeSlotSelect.displayName = 'TimeSlotSelect';

const requirePreregistration = (ticket: Ticket) => ticket.preregistration && ticket.current_max_per_order === 0;

/**
 * Inserts a given ticket into the list of registrations and sorts all registrations.
 */
const insertRegistration = (
  registrations: CreateRegistrationInput[],
  ticket: Ticket,
  promotion: Promotion,
  timeSlot: TimeSlot | undefined,
  quantity: number,
  products: Product[],
  assigned: boolean,
) => {
  const sameRegistrations: CreateRegistrationInput[] = [];
  const otherRegistrations: CreateRegistrationInput[] = [];

  registrations.forEach((registration) => {
    if (registration.purchase.promotion.id === promotion.id
      && (!timeSlot || registration.time_slot?.id === timeSlot.id)
    ) {
      if (sameRegistrations.length < quantity) {
        sameRegistrations.push(registration);
      }
    } else {
      otherRegistrations.push(registration);
    }
  });

  const missingQuantity = quantity - sameRegistrations.length;

  // Add automatic ticket fees and required products as upgrades
  const upgrades = getAdditionalProducts(promotion, products);

  const result = [
    ...otherRegistrations,
    ...sameRegistrations,
    ...times(missingQuantity, () => ({
      ticket: { id: ticket.id },
      ...(timeSlot ? {
        time_slot: { id: timeSlot.id },
      } : {}),
      purchase: {
        promotion: { id: promotion.id },
      },
      participant: assigned ? {} : null,
      details: assigned ? {} : null,
      fields: assigned ? [] : null,
      upgrades,
    })),
  ];

  return result;
};

const SpotsLeftLabel = ({ spotsLeft }: { spotsLeft: number; }) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();

  return (
    <UI.Label
      sc={{
        background: spotsLeft > 100
          ? 'primary.25'
          : (spotsLeft === 0 ? 'error.50' : 'info.100'),
        color: spotsLeft > 100
          ? 'primary.800'
          : (spotsLeft === 0 ? 'error.700' : 'info.800'),
      }}
    >
      {spotsLeft === 0 ? t('selector.full') : t('selector.n_spots_left', {
        count: spotsLeft,
        value: formatNumber(spotsLeft),
      })}
    </UI.Label>
  );
};

export default TicketSelector;
